import { Flex, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Component, ReactNode, useEffect, useRef, useState } from 'react';
import { fetchDocument } from 'slices/documentsActions';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { Button, Space, Tag } from 'antd';
import { ArrowLeftOutlined, FileOutlined } from '@ant-design/icons';
import { closeModal } from 'slices/modalsSlice';
import { ViewerAppryze, useToolBarDefault } from '@sitemax/smx-drawings-web';
import { useAccess } from '@sitemax/smx-permission';
import { saveMarkupAsVersion } from 'slices/documentsActions';

class ViewerErrorBoundary extends Component<
    { children: ReactNode; fallback?: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('ViewerAppryze crashed:', error, info);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? <div style={{ padding: 24 }}>Unable to load the document viewer.</div>;
        }
        return this.props.children;
    }
}

export interface IDocumentMarkupModalProps {
    readonly?: boolean;
}

export interface IDocumentMarkupModalCallbacks {
    onSubmit?: () => void;
    onClose?: () => void;
}

export const DocumentMarkupModal = ({
    id,
    zIndex,
    callbacks,
    props,
}: {
    id: string;
    zIndex: number;
    props: IDocumentMarkupModalProps;
    callbacks: IDocumentMarkupModalCallbacks;
}) => {
    const dispatch = useAppDispatch();
    const { readonly = false } = props;
    const { onClose } = callbacks;
    const { document } = useAppSelector((state) => state.document);
    const [isOpen, setIsOpen] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
    const canEdit = useAccess(document?.is_project_document ? ['files.project.edit'] : ['files.all.edit']);
    const [triggerSave, setTriggerSave] = useState(false);
    const pendingSaveRef = useRef<(() => void) | null>(null);
    const [updateAnnotations, setUpdateAnnotations] = useState(false);
    const [loading, setLoading] = useState(false);
    const [canMarkup, setCanMarkup] = useState(false);
    const [hadChanges, setHadChanges] = useState(false);

    useEffect(() => {
        setCanMarkup(!readonly && canEdit && document?.mime_type === 'application/pdf');
    }, [readonly, canEdit, document?.mime_type]);

    useEffect(() => {
        if (id) {
            setIsOpen(true);
        }
    }, [id]);

    useEffect(() => {
        /**
         * Only when opened
         */
        if (!isOpen) return;
        /**
         * Fetch Document URL
         * We did setDocument before open this modal,
         * but this Document doesn't have URL,
         * because the generation of temporary AWS URL is time consuming,
         * so we do that on demand.
         */
        if (!document?.url) {
            dispatch(fetchDocument(document?.id));
        }
    }, [isOpen]);

    /**
     * Handles the action when the close event is triggered.
     */
    const handleOnClose = () => {
        onClose?.();
        close();
    };

    /**
     * Close the modal
     */
    const close = () => {
        if (updateAnnotations) {
            setShowConfirmModal(true);
        } else {
            dispatch(closeModal({ modal: 'documentMarkupModal', id }));
        }
    };

    /**
     * Kicks off the viewer's buffered flush. The new version is cut in
     * finalizeSave, which the viewer calls once the writes have actually landed
     * - this used to be an unconditional 2s wait, which raced the mutations.
     */
    const handleSave = (callback?: () => void) => {
        if (!document?.id) return;
        setLoading(true);
        pendingSaveRef.current = callback ?? null;
        setTriggerSave(true);
        setHadChanges(true);
    };

    /**
     * Invoked by ViewerAppryze after every buffered annotation write resolves.
     *
     * `succeeded` is false when a write failed, errored or timed out. Cutting a
     * version then would flatten whatever the store still holds - the pre-edit
     * state - and report success, silently losing the user's markup.
     */
    const finalizeSave = async (succeeded: boolean = true) => {
        setTriggerSave(false);
        setUpdateAnnotations(false);

        const continuation = pendingSaveRef.current;
        pendingSaveRef.current = null;

        if (!document?.id) {
            setLoading(false);
            return;
        }

        if (!succeeded) {
            window.alert('Your markup could not be saved, so no new version was created. Please try again.');
            continuation?.();
            setLoading(false);
            return;
        }

        const result = await dispatch(saveMarkupAsVersion(document.id));
        if (result.meta.requestStatus === 'rejected') {
            // The API distinguishes "markup is off in this environment" (501) and
            // "the markup service is unreachable" (502) from a genuine fault, and
            // says which in the message. Showing it beats a generic error that
            // reads like a bug in whatever you were working on.
            const message = (result.payload as any)?.data?.message;
            window.alert(message || 'An error occurred while saving the markup');
        } else {
            window.alert('Markup saved successfully');
        }

        callbacks?.onSubmit?.(); // from parent component
        continuation?.();
        setLoading(false);
    };

    /**
     * Handle save and close
     */
    const handleSaveAndClose = () => {
        setTriggerSave(true);
        setShowConfirmModal(false);
        handleSave(() => {
            dispatch(closeModal({ modal: 'documentMarkupModal', id }));
        });
    };

    /**
     * Handle close without saving
     */
    const handleCloseWithoutSaving = () => {
        dispatch(closeModal({ modal: 'documentMarkupModal', id }));
        setShowConfirmModal(false);
    };

    /**
     * Handle cancel confirmation
     */
    const handleCancelConfirmation = () => {
        setShowConfirmModal(false);
        setShowSaveConfirmModal(false);
    };

    const { getToolBarDefault } = useToolBarDefault();

    const title = (
        <Flex style={{ position: 'relative' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', paddingBottom: 15 }}>
                <Space align="center">
                    <Button type="primary" icon={<ArrowLeftOutlined />} onClick={close} style={{ marginRight: 10 }} />
                    View document
                    <Tag icon={<FileOutlined />} color="blue">
                        {document?.name}
                    </Tag>
                </Space>
            </Space>
        </Flex>
    );

    return (
        <Modal
            zIndex={zIndex}
            title={title}
            open={isOpen}
            onCancel={handleOnClose}
            closable={false}
            confirmLoading={loading}
            footer={
                canMarkup && (
                    <Button type="primary" disabled={!updateAnnotations} onClick={() => {
                        setShowSaveConfirmModal(true);
                    }}>
                        Save
                    </Button>
                )
            }
            style={{ padding: 0 }}
            width="100%"
            centered
        >
            <ViewerErrorBoundary>
                <ViewerAppryze
                    fileName={document?.name}
                    toolBar={getToolBarDefault}
                    file={document?.base_url || document?.url || null}
                    sheetData={{ documentId: document?.id }}
                    height={'calc(100vh - 130px)'}
                    context={'document'}
                    allowedAnnotation={canMarkup}
                    bufferAnnotation={true}
                    onSave={finalizeSave}
                    onUpdateAnnotations={(flag: boolean) => {
                        setUpdateAnnotations(flag);
                    }}
                    triggerSave={triggerSave}
                />
            </ViewerErrorBoundary>

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        Save Changes?
                    </div>
                }
                open={showConfirmModal}
                onCancel={handleCancelConfirmation}
                footer={[
                    <Button key="cancel" onClick={handleCancelConfirmation}>
                        Cancel
                    </Button>,
                    <Button key="no" onClick={handleCloseWithoutSaving}>
                        No
                    </Button>,
                    <Button key="yes" type="primary" onClick={handleSaveAndClose}>
                        Yes
                    </Button>,
                ]}
                centered
            >
                <p>You have unsaved markup changes. Do you want to save your changes and create a new version before closing?
                Warning: without creating a version your markup will not be saved with the PDF.</p>
            </Modal>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        Save Changes?
                    </div>
                }
                open={showSaveConfirmModal}
                onCancel={handleCancelConfirmation}
                footer={[
                    <Button key="cancel" onClick={handleCancelConfirmation}>
                        Cancel
                    </Button>,
                    <Button key="yes" type="primary" onClick={() => {
                        setShowSaveConfirmModal(false);
                        handleSave();
                    }}>
                        Yes
                    </Button>,
                ]}
                centered
            >
                <p>Saving your changes will create a new version - please make sure you have all desired changes. Do you want to create a new version now?</p>
            </Modal>
        </Modal>
    );
};
